/**
 * Sidebar P2b (2026-05-21): pinned/starred nav items.
 *
 * Users click a star icon next to any nav item to pin it. Pinned items
 * appear in a "Pinned" group at the top of the sidebar (above all
 * functional groups, below the attention group). Persists per-user via
 * localStorage; not synced cross-device (matches Sidebar P1 pattern of
 * not persisting view-state that should not roam).
 *
 * Storage: array of route paths (e.g. ["/finance/bills", "/tax"]).
 * Pinned group hides entirely when no items are pinned.
 *
 * Cross-component sync: dispatches `nexus:nav-pinned-changed` custom
 * event on every change so any component (Settings card, mobile bottom
 * nav once it ships) re-reads.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'nexus_nav_pinned_routes';
const EVENT_NAME = 'nexus:nav-pinned-changed';

function loadPinned(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string' && x.startsWith('/'));
  } catch {
    return [];
  }
}

function savePinned(routes: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    // localStorage unavailable (private window?). Ignore — pinning is UX, not data.
  }
}

export interface UsePinnedNavReturn {
  /** Set of pinned route paths (e.g. "/finance/bills"). */
  pinnedRoutes: Set<string>;
  /** True if the given route path is currently pinned. */
  isPinned: (route: string) => boolean;
  /** Toggle pin state for the route. */
  togglePin: (route: string) => void;
  /** Pin a route (idempotent — no-op if already pinned). */
  pin: (route: string) => void;
  /** Unpin a route (idempotent — no-op if not pinned). */
  unpin: (route: string) => void;
  /** Clear all pins. */
  clear: () => void;
}

export function usePinnedNav(): UsePinnedNavReturn {
  const [routes, setRoutes] = useState<string[]>(() => loadPinned());

  // Subscribe to cross-component change events so any other instance of
  // this hook (e.g. Settings card, mobile nav) stays in sync.
  useEffect(() => {
    const onChange = () => setRoutes(loadPinned());
    window.addEventListener(EVENT_NAME, onChange);
    // Also handle the cross-tab storage event so changes in one tab
    // appear immediately in another.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setRoutes(loadPinned());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Cycle-1 fix (2026-05-21): memoize the Set so a stable reference flows
  // to consumers. Without useMemo, a fresh Set is allocated every render
  // and any consumer that uses pinnedRoutes as a useMemo dep (e.g.
  // SideNav.tsx's [visibleGroups, pinnedRoutes] memo for the rendered
  // groups + pinned overlay) invalidates on every render — defeating the
  // memoization. Functionally identical output, materially cheaper.
  const pinnedRoutes = useMemo(() => new Set(routes), [routes]);

  const isPinned = useCallback((route: string) => pinnedRoutes.has(route), [pinnedRoutes]);

  const togglePin = useCallback((route: string) => {
    const current = loadPinned();
    const next = current.includes(route)
      ? current.filter(r => r !== route)
      : [...current, route];
    savePinned(next);
    setRoutes(next);
  }, []);

  const pin = useCallback((route: string) => {
    const current = loadPinned();
    if (current.includes(route)) return;
    const next = [...current, route];
    savePinned(next);
    setRoutes(next);
  }, []);

  const unpin = useCallback((route: string) => {
    const current = loadPinned();
    if (!current.includes(route)) return;
    const next = current.filter(r => r !== route);
    savePinned(next);
    setRoutes(next);
  }, []);

  const clear = useCallback(() => {
    savePinned([]);
    setRoutes([]);
  }, []);

  return { pinnedRoutes, isPinned, togglePin, pin, unpin, clear };
}
